import { TeamAPI } from "../../../../../../../src/apis/TeamAPI"
import { GetStaticPropsContext } from "next"
import { Col, Row } from "react-bootstrap"
import React, { useMemo } from "react"
import { TeamPlayers } from "@components/Team/TeamPlayers"
import { TeamDetails } from "@components/Team/TeamDetails/TeamDetails"
import { getNavigationPropsForTeam } from "@components/Navigation/getNavigationProps"
import { SeasonAPI } from "../../../../../../../src/apis/SeasonAPI"
import { SEO } from "../../../../../../../src/types/next.js"
import { ParsedUrlQuery } from "querystring"
import InferNextPropsType from "infer-next-props-type"
import * as NextConstants from "../../../../../../../src/constants/next"
import { CompetitionAPI } from "../../../../../../../src/apis/CompetitionAPI"
import { TeamMenu } from "@components/Team/TeamMenu/TeamMenu"
import { getTeamCanonicalData, simplifyTeam } from "../../../../../../../src/utils/team"
import { simplifyCompetition } from "utils/competition"
import { simplifySeason } from "utils/season"
import { simplifyTeamPlayer } from "utils/player"
import { simplifyCompetitionSeason } from "utils/competitionSeason"

export default function TeamPage({
  team,
  competitionCalciocomSlug,
  seasonCalciocomSlug,
  players,
  competitionSeasons,
}: InferNextPropsType<typeof getStaticProps>) {
  const currentCompetition = useMemo(() => {
    return competitionSeasons.find((c) => c.competition?.calciocomSlug === competitionCalciocomSlug)?.competition
  }, [competitionCalciocomSlug])
  const currentSeason = useMemo(() => {
    return competitionSeasons.find((c) => c.season?.calciocomSlug === seasonCalciocomSlug)?.season
  }, [seasonCalciocomSlug])

  return (
    <>
      <Row className={"marginZero paddingZero"}>
        <TeamDetails team={team} />
      </Row>
      <TeamMenu
        selected="formazione"
        teamCalciocomSlug={team.calciocomSlug!}
        competitionSeasons={competitionSeasons}
        team={team}
        competitionCalciocomSlug={competitionCalciocomSlug}
        seasonCalciocomSlug={seasonCalciocomSlug}
        page={"formazione"}
        title={`${team.name} Formazione ${currentCompetition?.name} - Stagione ${currentSeason?.name}`}
      />
      <Row className={"marginZero"}>
        <Col md={12} className={"paddingFive"}>
          <div id="hs-content" className={"hs-border-div paddingZero"}>
            <TeamPlayers players={players} />
          </div>
        </Col>
      </Row>
    </>
  )
}

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" }
}

export interface IParams extends ParsedUrlQuery {
  teamCalciocomSlug: string
  competitionCalciocomSlug: string
  seasonCalciocomSlug: string
}

export async function getStaticProps(context: GetStaticPropsContext<IParams>, isLastAvailableSeason = false) {
  try {
    const { teamCalciocomSlug, competitionCalciocomSlug, seasonCalciocomSlug } = context.params!

    const [teamIds, competitionIds, seasonIds] = await Promise.all([
      TeamAPI.getTeamIds(teamCalciocomSlug),
      CompetitionAPI.getCompetitionIds(competitionCalciocomSlug),
      SeasonAPI.getSeasonIds(seasonCalciocomSlug),
    ])

    const [rawCompetition, rawSeason] = await Promise.all([
      CompetitionAPI.getCompetition(competitionIds.id, false),
      SeasonAPI.getSeason(seasonIds.id),
    ])

    const competition = simplifyCompetition(rawCompetition)
    const season = simplifySeason(rawSeason)

    const [rawTeam, rawPlayers, rawCompetitionSeason] = await Promise.all([
      TeamAPI.getTeam(teamIds.id, false),
      TeamAPI.getTeamPlayersBySeason(teamIds.id, seasonIds.id),
      TeamAPI.getCompetitionSeasons(teamIds.id, false, true),
    ])
    const team = simplifyTeam(rawTeam)
    const players = rawPlayers.map((p) => simplifyTeamPlayer(p))
    const competitionSeasons = rawCompetitionSeason.map((cs) => simplifyCompetitionSeason(cs))

    const title = `${team.completeName}:  Formazione Stagione ${season.name}`
    const seo: SEO = {
      title: title,
      description: `La formazione al completo di ${team.completeName} per il Campionato di ${competition.name} della stagione ${season.name}, con i nomi dei calciatori, la nazionalità, data di nascita peso e altezza.`,
      keywords: [
        `${team.name}`,
        "calcio",
        season.year.toString(),
        "Rosa",
        "Calendario",
        "Club",
        "Squadra",
        "informazioni",
      ],
      ...(await getTeamCanonicalData(
        team,
        season,
        `/squadre/${teamCalciocomSlug}/campionato/${competitionCalciocomSlug}/stagione/${seasonCalciocomSlug}/formazione`,
        isLastAvailableSeason,
      )),
      canonicals: [`/squadre/${teamCalciocomSlug}/formazione`],
    }

    return {
      props: {
        team,
        competitionCalciocomSlug,
        seasonCalciocomSlug,
        players,
        competitionSeasons,
        navigationProps: await getNavigationPropsForTeam(team.id, competition.id),
        seo,
        title,
        notFound: false,
      },
      revalidate: NextConstants.REVALIDATE,
    }
  } catch (error) {
    console.error(error)
    return {
      notFound: true,
    }
  }
}
